import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { MenuItem } from '@material-ui/core';
import Placeholder from '../common/Placeholder';
import CreateTaskDialog from './CreateTaskDialog';
import AddProjectDialog from './AddProjectDialog';
import { ENTITY_TYPE_FILE } from '../../core/entities';
import {
  createFluidTopicsImportTask,
  fetchFluidTopicsPing,
} from '../../redux/actions/taskActions';
import { applySearch, parsePath } from '../../core/services/fileStorageService';
import { getObjectsFromPath, getPathEntity } from '../../redux/actions/fileStorageActions';
import { TASKS_ROUTE } from '../../core/constants';
import { getCorrectPath } from '../common/Utility';

const STEP_FIELD_TYPE_MULTIPLE = 'multiple';
const STEP_FIELD_TYPE_PATH = 'path';

function filterObjects(objects, searchValue) {
  return applySearch(objects, searchValue).sort((a, b) => b.type.localeCompare(a.type));
}

const validationSchema = () => {
  const schema = {
    project: yup.string().required('Project is required'),
    sourceId: yup.string().required('The source ID is required'),
    serverUrl: yup.string().required('The server URL is required'),
    apiKey: yup.string().required('The server API key is required'),
    sourceFilePath: yup.string()
      .required('Source file path is required')
      .test(
        'start_end_with_space',
        'The element of the source file path can not start or end with spaces',
        function (v) {
          const value = getCorrectPath(v, this.parent.sourceFilePathSearchValue);

          let result = true;
          value.split('/').forEach(path => {
            if (path.startsWith(' ') || path.endsWith(' ')) {
              result = false;
            }
          });

          return result;
        },
      )
      .test(
        'allowed-characters',
      <>
        Source file name can contain only the next characters:
        <br />
        {'0-9, a-z, A-Z, space, \'.\', \'-\', \'_\' and \'/\'.'}
      </>,
      function (v) {
        const { sourceFilePathSearchValue } = this.parent;
        const value = `${v}/${sourceFilePathSearchValue}`;

        return /^[a-zA-Z0-9\s/\-._]+$/.test(value);
      },
      )
      .test(
        'not-new-lines',
        'Source file name cannot contain the new lines',
        function (v) {
          const { sourceFilePathSearchValue } = this.parent;
          const value = `${v}/${sourceFilePathSearchValue}`;

          return value && !value.includes('\n');
        },
      )
      .test(
        'file-exist',
        'Source file does not exist or is not a file entity',
        async function (v) {
          const { sourceFilePathSearchValue } = this.parent;
          const value = `${v}/${sourceFilePathSearchValue}`;

          if (value) {
            const response = await getPathEntity(value);

            return response.status === 200 && response.data.data.type === ENTITY_TYPE_FILE;
          }

          return false;
        },
      ),
  };

  return yup.object(schema);
};

const generateInitValues = task => {
  const config = JSON.parse(task.config || '{}');

  const [sourceFilePath, sourceFilename] = parsePath(task.source || '');

  return {
    // external settings
    apiKey: '',
    sourceId: config.source_id || '',
    serverUrl: config.server_url || '',
    isCollection: config.is_collection || false,

    sourceFilePath: sourceFilePath || '',
    sourceFilePathSearchValue: sourceFilename || '',
    sourceFilePathObjects: [],

    name: task.name || '',

    project: task.project_id || '',
  };
};

function CreateFluidTopicsDialog(props) {
  const {
    open,
    onClose,
    rerunTask,
    resetOnClose,
  } = props;

  const dispatch = useDispatch();

  const [creatingTask, setCreatingTask] = useState(false);
  const [loadingSourceFileObjects, setLoadingSourceFileObjects] = useState(false);

  const [firstStepError, setFirstStepError] = useState('');

  const [openAddProjectDialog, setOpenAddProjectDialog] = useState(false);

  const formik = useFormik({
    initialValues: generateInitValues(rerunTask || {}),
    validationSchema: validationSchema(),
    validateOnChange: false,
    validateOnMount: false,
    validateOnBlur: false,
  });

  const projects = useSelector(state => state.projects.projects);

  const handleClose = link => {
    onClose(link);

    formik.resetForm();
  };

  const onFormSubmit = values => {
    setCreatingTask(true);

    const task = {
      source: `${values.sourceFilePath}${values.sourceFilePath.endsWith('/') ? '' : '/'}${values.sourceFilePathSearchValue}`,
      is_collection: values.isCollection,
      server_url: values.serverUrl,
      source_id: values.sourceId,
      api_key: values.apiKey,
      name: values.name,
      project_id: values.project,
    };

    dispatch(createFluidTopicsImportTask(task))
      .then(res => handleClose(`${TASKS_ROUTE}/${res.id}`))
      .catch(() => setCreatingTask(false));
  };

  const onCreateButtonClick = () => {
    if (creatingTask) return;
    setCreatingTask(true);

    onFormSubmit(formik.values);
  };

  const onChangePathValue = (value, fieldName, searchFieldName) => {
    const split = value.split('/');

    const search = split[split.length - 1];
    const path = `${split.slice(0, -1).join('/')}`;

    if (path !== '/' || formik.values[fieldName] !== path) formik.setFieldValue(fieldName, path);
    formik.setFieldValue(searchFieldName, search).then(() => formik.validateField(fieldName));
  };

  const config = {
    title: 'Create Fluid Topics Import',
    steps: [
      {
        type: STEP_FIELD_TYPE_MULTIPLE,
        title: 'General settings',
        fields: [
          {
            onChange: e => formik.handleChange(e),
            value: formik.values.name,
            error: formik.errors.name,
            placeholder: 'Task Name',
            label: 'Task Name (Optional)',
            key: 'name',
          },
          {
            onChange: e => formik.handleChange(e),
            value: formik.values.project,
            displayValue: (() => {
              const selected = projects.find(p => p.id === formik.values.project);
              return selected ? selected.name : '';
            })(),
            error: formik.errors.project,
            renderValue: value => {
              if (!value) return <Placeholder>Project</Placeholder>;
              const selected = projects.find(p => p.id === value);
              return selected ? selected.name : '';
            },
            label: 'Project',
            onAdd: () => setOpenAddProjectDialog(true),
            key: 'project',
            item: selectedProject => (
              <MenuItem key={selectedProject.id} value={selectedProject.id}>
                {selectedProject.name}
              </MenuItem>
            ),
            items: projects,
          },
          {
            onChange: event => formik.handleChange(event),
            value: formik.values.serverUrl,
            error: formik.errors.serverUrl,
            placeholder: 'Server URL',
            label: 'Server URL',
            key: 'serverUrl',
          },
          {
            onChange: event => formik.handleChange(event),
            value: formik.values.apiKey,
            error: formik.errors.apiKey,
            placeholder: 'API Key',
            type: 'password',
            label: 'API Key',
            key: 'apiKey',
          },
          {
            onChange: event => formik.handleChange(event),
            value: formik.values.sourceId,
            error: formik.errors.sourceId,
            placeholder: 'Source ID',
            label: 'Source ID',
            key: 'sourceId',
          },
        ],
        configurations: [
          {
            tooltip: 'Use this option if the source archive contains the archives.',
            onChange: event => formik.handleChange(event),
            value: formik.values.isCollection,
            label: 'Is Collection',
            key: 'isCollection',
          },
        ],
        isValid: (formik.values.serverUrl !== '' && formik.errors.serverUrl === undefined)
          && (formik.values.sourceId !== '' && formik.errors.sourceId === undefined)
          && (formik.values.apiKey !== '' && formik.errors.apiKey === undefined)
          && (formik.values.project !== '' && formik.errors.project === undefined),
        async onSubmit() {
          const value = await fetchFluidTopicsPing(formik.values.serverUrl, formik.values.apiKey);

          if (!value) {
            setFirstStepError('Unable to connect to the server. Please check if server is running and credentials are correct.');
          }

          return value;
        },
        allowContinue: formik.values.project !== undefined,
        error: firstStepError,
        loading: false,
      },
      {
        title: 'Source file path',
        type: STEP_FIELD_TYPE_PATH,
        pathField: {
          objects: filterObjects(
            formik.values.sourceFilePathObjects, formik.values.sourceFilePathSearchValue,
          ),
          onChange: value => onChangePathValue(value, 'sourceFilePath', 'sourceFilePathSearchValue'),
          value: formik.values.sourceFilePath !== ''
            ? `${formik.values.sourceFilePath}${formik.values.sourceFilePath.endsWith('/') ? '' : '/'}${formik.values.sourceFilePathSearchValue}`
            : formik.values.sourceFilePathSearchValue,
          error: formik.errors.sourceFilePath,
          loading: false,
        },
        allowContinue: formik.values.sourceFilePath !== '',
        isValid: formik.errors.sourceFilePath === undefined,
        onSubmit: () => Promise.resolve(true),
        loading: loadingSourceFileObjects,
      },
    ],
    onSubmit: () => onCreateButtonClick(),
    allowAnyStepSelection: true,
    loading: creatingTask,
    allowContinue: true,
    initActiveStep: 0,
  };

  useEffect(() => {
    if (open) {
      setLoadingSourceFileObjects(true);

      getObjectsFromPath(`/${formik.values.sourceFilePath}`)
        .then(objects => {
          formik.setFieldValue('sourceFilePathObjects', objects.sort((a, b) => b.type.localeCompare(a.type)))
            .then(() => {
              formik.validateField('sourceFilePath').then(() => setLoadingSourceFileObjects(false));
            });
        });
    }
  }, [formik.values.sourceFilePath, open]);

  useEffect(() => {
    if (open && formik.values.serverUrl !== '') formik.validateField('serverUrl');
  }, [formik.values.serverUrl]);

  useEffect(() => {
    if (open && formik.values.sourceId !== '') formik.validateField('sourceId');
  }, [formik.values.sourceId]);

  useEffect(() => {
    if (open && formik.values.apiKey !== '') formik.validateField('apiKey');
  }, [formik.values.apiKey]);

  const handleAddProject = (name, type, defaultValue) => {
    formik.setFieldValue(`config.${name}`, { value: defaultValue || '', type });
  };

  return (
    <>
      <CreateTaskDialog
        open={open}
        config={config}
        onClose={() => {
          onClose();

          if (resetOnClose) {
            formik.setValues(generateInitValues({}));
          }
        }}
        resetOnClose={resetOnClose}
      />
      <AddProjectDialog
        open={openAddProjectDialog}
        onClose={() => setOpenAddProjectDialog(false)}
        onSubmit={handleAddProject}
        existingProjectNames={projects.map(p => p.name)}
      />
    </>
  );
}

CreateFluidTopicsDialog.defaultProps = { rerunTask: {}, resetOnClose: true };

CreateFluidTopicsDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  resetOnClose: PropTypes.bool,
  rerunTask: PropTypes.object,
};

export default CreateFluidTopicsDialog;
